import React, { useEffect } from "react";
import "../css/components/PrivacyPolicy&TermsOfUse.css";

const TermsOfService = () => {
  const lastUpdated = "December 15, 2024";

  useEffect(() => {
    // Add scroll progress indicator
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      const statusBar = document.querySelector(".status-bar");
      if (statusBar) {
        statusBar.style.transform = `scaleX(${scrollPercent / 100})`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <div className="status-bar"></div>
      <div className="privacy-background-page">
        <div className="terms-of-service-container">
          {/* Enhanced Header Section */}
          <div className="policy-header">
            <h1 className="terms-of-service-title">Terms of Service</h1>
            <p className="terms-of-service-date">Last Updated: {lastUpdated}</p>
            <p className="terms-of-service-intro">
              Welcome to CollegeNav, your comprehensive educational platform
              designed to guide students through their college journey. These
              Terms of Service constitute a legally binding agreement between
              you and CollegeNav, governing your access to and use of our
              website, services, and educational resources. By accessing,
              browsing, or using any part of our platform, you acknowledge that
              you have read, understood, and agree to be bound by these terms.
              If you do not agree with any part of these terms, please
              discontinue use of our services immediately. For questions
              regarding these terms, please contact us at{" "}
              <a
                href="mailto:collegenav.info@gmail.com"
                className="terms-of-service-email"
              >
                collegenav.info@gmail.com
              </a>
              .
            </p>
          </div>

          {/* Enhanced Table of Contents */}
          <div className="table-of-contents">
            <h3 className="toc-title">Table of Contents</h3>
            <ul className="toc-list">
              <li className="toc-item">
                <a
                  href="#section-1"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-1");
                  }}
                >
                  1. Acceptance of Terms
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-2"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-2");
                  }}
                >
                  2. Description of Services
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-3"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-3");
                  }}
                >
                  3. User Accounts & Registration
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-4"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-4");
                  }}
                >
                  4. User Conduct & Responsibilities
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-5"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-5");
                  }}
                >
                  5. Intellectual Property Rights
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-6"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-6");
                  }}
                >
                  6. Privacy & Data Protection
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-7"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-7");
                  }}
                >
                  7. Service Availability & Modifications
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-8"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-8");
                  }}
                >
                  8. Disclaimers & Limitation of Liability
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-9"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-9");
                  }}
                >
                  9. Indemnification
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-10"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-10");
                  }}
                >
                  10. Governing Law & Dispute Resolution
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-11"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-11");
                  }}
                >
                  11. Termination & Suspension
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-12"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-12");
                  }}
                >
                  12. Changes to Terms of Service
                </a>
              </li>
              <li className="toc-item">
                <a
                  href="#section-13"
                  className="toc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection("section-13");
                  }}
                >
                  13. Contact Information
                </a>
              </li>
            </ul>
          </div>

          {/* Enhanced Content Sections */}
          <section id="section-1" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="01">
              1. Acceptance of Terms
            </h2>
            <p className="terms-of-service-text">
              By accessing, browsing, or using CollegeNav in any manner, you
              acknowledge that you have read, understood, and agree to be
              legally bound by these Terms of Service and all applicable laws
              and regulations. These terms apply to all users of the service,
              including without limitation users who are browsers, vendors,
              customers, merchants, and contributors of content. If you do not
              agree to these terms, you are prohibited from using or accessing
              this site and must discontinue use immediately.
            </p>
            <p className="terms-of-service-text">
              Your continued use of our services following any modifications to
              these terms constitutes acceptance of those changes. We reserve
              the right to update, change, or replace any part of these Terms of
              Service by posting updates and changes to our website.
            </p>
          </section>

          <section id="section-2" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="02">
              2. Description of Services
            </h2>
            <p className="terms-of-service-text">
              CollegeNav provides a comprehensive educational platform designed
              to assist students, parents, and educators in navigating the
              college preparation, application, and selection process. Our
              services include but are not limited to college search tools,
              application guidance, scholarship information, educational
              resources, career planning tools, and personalized
              recommendations.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Educational Resources:</strong> Access to comprehensive
                guides, articles, videos, and interactive tools covering college
                preparation, application processes, financial aid, and career
                planning.
              </li>
              <li className="terms-of-service-item">
                <strong>Personalized Recommendations:</strong> Customized
                college and scholarship recommendations based on your academic
                profile, interests, and preferences.
              </li>
              <li className="terms-of-service-item">
                <strong>Application Support:</strong> Tools and guidance to help
                streamline the college application process, including essay
                assistance and deadline tracking.
              </li>
              <li className="terms-of-service-item">
                <strong>Community Features:</strong> Access to forums,
                discussion boards, and peer support networks to connect with
                other students and educational professionals.
              </li>
            </ul>
            <p className="terms-of-service-text">
              We reserve the right to modify, suspend, or discontinue any aspect
              of our services at any time without prior notice. We may also
              impose limits on certain features or restrict access to parts or
              all of the service without notice or liability.
            </p>
          </section>

          <section id="section-3" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="03">
              3. User Accounts & Registration
            </h2>
            <p className="terms-of-service-text">
              To access certain features of CollegeNav, you may be required to
              create a user account. When creating an account, you agree to
              provide accurate, current, and complete information and to
              maintain and update this information to keep it accurate and
              complete.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Account Registration:</strong> You must provide accurate
                and complete information during the registration process,
                including a valid email address. You are responsible for
                maintaining the accuracy of your account information and
                promptly updating any changes.
              </li>
              <li className="terms-of-service-item">
                <strong>Account Security:</strong> You are solely responsible
                for maintaining the confidentiality of your account credentials,
                including your username and password. You agree to notify us
                immediately of any unauthorized use of your account or any other
                breach of security.
              </li>
              <li className="terms-of-service-item">
                <strong>Account Responsibility:</strong> You are fully
                responsible for all activities that occur under your account,
                whether or not you authorized such activities. You agree to
                accept responsibility for all activities that occur under your
                account.
              </li>
              <li className="terms-of-service-item">
                <strong>Account Termination:</strong> We reserve the right to
                refuse service, terminate accounts, remove or edit content, or
                cancel orders at our sole discretion, particularly if you
                violate these terms or engage in conduct that we determine to be
                inappropriate or harmful to our community.
              </li>
            </ul>
          </section>

          <section id="section-4" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="04">
              4. User Conduct & Responsibilities
            </h2>
            <p className="terms-of-service-text">
              As a user of CollegeNav, you agree to use our services responsibly
              and in accordance with these terms. You are expected to maintain
              high standards of conduct and respect for other users and our
              educational community.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Prohibited Activities:</strong> You agree not to engage
                in any illegal, harmful, or disruptive activities, including but
                not limited to: uploading malicious content, spamming, hacking,
                attempting to gain unauthorized access, impersonating others, or
                distributing copyrighted material without permission.
              </li>
              <li className="terms-of-service-item">
                <strong>Content Standards:</strong> Any content you submit must
                be appropriate for an educational environment, respectful of
                others, and comply with applicable laws. You may not post
                content that is offensive, discriminatory, threatening, or
                violates the rights of others.
              </li>
              <li className="terms-of-service-item">
                <strong>User-Generated Content:</strong> You retain ownership of
                content you submit to CollegeNav, but you grant us a
                non-exclusive, worldwide, royalty-free, perpetual license to
                use, reproduce, modify, adapt, publish, translate, distribute,
                and display your content in connection with our services.
              </li>
              <li className="terms-of-service-item">
                <strong>Monitoring and Enforcement:</strong> We reserve the
                right to monitor user activity, review content, and enforce
                these terms. We may remove content, suspend accounts, or take
                other appropriate action for violations of these terms or
                applicable laws.
              </li>
              <li className="terms-of-service-item">
                <strong>Reporting Violations:</strong> Users are encouraged to
                report any violations of these terms or inappropriate behavior
                to our support team. We take all reports seriously and will
                investigate and respond appropriately.
              </li>
            </ul>
          </section>

          <section id="section-5" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="05">
              5. Intellectual Property Rights
            </h2>
            <p className="terms-of-service-text">
              CollegeNav and its content, features, and functionality are owned
              by CollegeNav and are protected by United States and international
              copyright, trademark, patent, trade secret, and other intellectual
              property laws.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Our Content:</strong> All content on CollegeNav,
                including but not limited to text, graphics, logos, images,
                audio clips, video clips, data compilations, software, and the
                compilation of all content, is the property of CollegeNav or its
                content suppliers and is protected by intellectual property
                laws.
              </li>
              <li className="terms-of-service-item">
                <strong>Limited License:</strong> We grant you a limited,
                non-exclusive, non-transferable license to access and use our
                services for personal, non-commercial purposes in accordance
                with these terms. This license does not include any right to
                resell or make commercial use of our services or content.
              </li>
              <li className="terms-of-service-item">
                <strong>Restrictions:</strong> You may not reproduce,
                distribute, modify, create derivative works of, publicly
                display, publicly perform, republish, download, store, or
                transmit any of our content without our express written
                permission.
              </li>
              <li className="terms-of-service-item">
                <strong>Trademarks:</strong> CollegeNav and related logos,
                trademarks, and service marks are the property of CollegeNav.
                You may not use these marks without our prior written consent.
              </li>
              <li className="terms-of-service-item">
                <strong>DMCA Compliance:</strong> We respect intellectual
                property rights and expect our users to do the same. If you
                believe your copyrighted work has been copied in a way that
                constitutes copyright infringement, please contact us with
                detailed information about the alleged infringement.
              </li>
            </ul>
          </section>

          <section id="section-6" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="06">
              6. Privacy & Data Protection
            </h2>
            <p className="terms-of-service-text">
              Your privacy is important to us. Our collection, use, and
              protection of your personal information is governed by our Privacy
              Policy, which is incorporated into these Terms of Service by
              reference. By using our services, you consent to our collection
              and use of your information as described in the Privacy Policy.
            </p>
            <p className="terms-of-service-text">
              We implement appropriate security measures to protect your
              personal information and maintain the confidentiality of your
              data. However, you acknowledge that no method of transmission over
              the internet or electronic storage is completely secure, and you
              use our services at your own risk.
            </p>
          </section>

          <section id="section-7" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="07">
              7. Service Availability & Modifications
            </h2>
            <p className="terms-of-service-text">
              We strive to provide reliable and consistent access to our
              services, but we cannot guarantee uninterrupted availability. Our
              services may be subject to limitations, delays, and other problems
              inherent in the use of the internet and electronic communications.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Service Modifications:</strong> We reserve the right to
                modify, suspend, or discontinue any aspect of our services at
                any time, with or without notice. We may also impose limits on
                certain features or restrict access to parts or all of the
                service.
              </li>
              <li className="terms-of-service-item">
                <strong>Maintenance and Updates:</strong> We may perform
                scheduled maintenance, updates, or improvements to our services,
                which may temporarily affect availability. We will make
                reasonable efforts to minimize disruptions and provide advance
                notice when possible.
              </li>
              <li className="terms-of-service-item">
                <strong>Third-Party Services:</strong> Our platform may
                integrate with or link to third-party services, websites, or
                applications. We are not responsible for the availability,
                content, or practices of these third-party services.
              </li>
            </ul>
          </section>

          <section id="section-8" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="08">
              8. Disclaimers & Limitation of Liability
            </h2>
            <p className="terms-of-service-text">
              Your use of CollegeNav is at your own risk. Our services are
              provided on an "as is" and "as available" basis without any
              warranties of any kind, either express or implied.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Disclaimer of Warranties:</strong> We disclaim all
                warranties, express or implied, including but not limited to
                implied warranties of merchantability, fitness for a particular
                purpose, and non-infringement. We do not warrant that our
                services will be uninterrupted, error-free, or completely
                secure.
              </li>
              <li className="terms-of-service-item">
                <strong>Educational Information:</strong> The information
                provided on CollegeNav is for educational and informational
                purposes only. We do not guarantee the accuracy, completeness,
                or timeliness of any information, and you should verify all
                information independently.
              </li>
              <li className="terms-of-service-item">
                <strong>Limitation of Liability:</strong> In no event shall
                CollegeNav, its affiliates, or their respective officers,
                directors, employees, or agents be liable for any indirect,
                incidental, special, consequential, or punitive damages,
                including but not limited to loss of profits, data, or use,
                arising out of or in connection with your use of our services.
              </li>
              <li className="terms-of-service-item">
                <strong>Maximum Liability:</strong> Our total liability to you
                for all claims arising out of or relating to these terms or your
                use of our services shall not exceed the amount you paid to us,
                if any, during the twelve months preceding the claim.
              </li>
            </ul>
          </section>

          <section id="section-9" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="09">
              9. Indemnification
            </h2>
            <p className="terms-of-service-text">
              You agree to defend, indemnify, and hold harmless CollegeNav, its
              affiliates, and their respective officers, directors, employees,
              agents, and representatives from and against any and all claims,
              damages, obligations, losses, liabilities, costs, and expenses
              (including but not limited to attorney's fees) arising from or
              related to:
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Your Use of Services:</strong> Your access to and use of
                our services, including any content you submit or actions you
                take on our platform.
              </li>
              <li className="terms-of-service-item">
                <strong>Violation of Terms:</strong> Your violation of these
                Terms of Service or any applicable laws or regulations.
              </li>
              <li className="terms-of-service-item">
                <strong>Infringement Claims:</strong> Your violation of any
                third-party rights, including without limitation any copyright,
                trademark, property, or privacy right.
              </li>
              <li className="terms-of-service-item">
                <strong>User Content:</strong> Any content you submit, post, or
                transmit through our services that causes harm to others or
                violates applicable laws.
              </li>
            </ul>
          </section>

          <section id="section-10" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="10">
              10. Governing Law & Dispute Resolution
            </h2>
            <p className="terms-of-service-text">
              These Terms of Service and any disputes arising out of or related
              to these terms or your use of our services shall be governed by
              and construed in accordance with the laws of the United States and
              the state in which CollegeNav operates, without regard to conflict
              of law principles.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Jurisdiction:</strong> You agree that any legal action
                or proceeding arising out of or related to these terms shall be
                brought exclusively in the federal or state courts located in
                our jurisdiction, and you consent to the personal jurisdiction
                of such courts.
              </li>
              <li className="terms-of-service-item">
                <strong>Dispute Resolution:</strong> We encourage users to
                contact us directly to resolve any disputes. If a dispute cannot
                be resolved through direct communication, you agree to attempt
                to resolve the dispute through mediation before pursuing
                litigation.
              </li>
              <li className="terms-of-service-item">
                <strong>Class Action Waiver:</strong> You agree that any dispute
                resolution proceedings will be conducted only on an individual
                basis and not in a class, consolidated, or representative
                action.
              </li>
            </ul>
          </section>

          <section id="section-11" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="11">
              11. Termination & Suspension
            </h2>
            <p className="terms-of-service-text">
              We reserve the right to terminate or suspend your account and
              access to our services at any time, with or without cause, and
              with or without notice, for any reason including but not limited
              to violation of these terms.
            </p>
            <ul className="terms-of-service-list">
              <li className="terms-of-service-item">
                <strong>Termination by Us:</strong> We may terminate your
                account immediately if you violate these terms, engage in
                fraudulent or illegal activities, or for any other reason at our
                sole discretion.
              </li>
              <li className="terms-of-service-item">
                <strong>Termination by You:</strong> You may terminate your
                account at any time by contacting us or using the account
                deletion features in your account settings.
              </li>
              <li className="terms-of-service-item">
                <strong>Effect of Termination:</strong> Upon termination, your
                right to use our services will cease immediately, and we may
                delete your account and all associated data, subject to
                applicable legal requirements.
              </li>
              <li className="terms-of-service-item">
                <strong>Survival:</strong> Provisions of these terms that by
                their nature should survive termination shall survive, including
                but not limited to intellectual property rights, disclaimers,
                and limitation of liability.
              </li>
            </ul>
          </section>

          <section id="section-12" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="12">
              12. Changes to Terms of Service
            </h2>
            <p className="terms-of-service-text">
              We reserve the right to modify these Terms of Service at any time
              to reflect changes in our services, legal requirements, or
              business practices. We are committed to providing you with notice
              of material changes to these terms.
            </p>
            <p className="terms-of-service-text">
              When we make significant changes to these Terms of Service, we
              will notify you through prominent notices on our website, email
              notifications, or other appropriate communication methods. We
              encourage you to review these terms periodically to stay informed
              about your rights and obligations. Your continued use of our
              services after any changes indicates your acceptance of the
              updated Terms of Service.
            </p>
          </section>

          <section id="section-13" className="terms-of-service-section">
            <h2 className="terms-of-service-heading" data-section="13">
              13. Contact Information
            </h2>
            <p className="terms-of-service-text">
              We value your feedback and are committed to addressing any
              questions or concerns you may have regarding these Terms of
              Service or our services. Our support team is available to assist
              you with any legal or service-related matters.
            </p>
            <p className="terms-of-service-text">
              If you have any questions, concerns, or requests regarding these
              Terms of Service, our services, or any legal matters, please
              contact us at{" "}
              <a
                href="mailto:collegenav.info@gmail.com"
                className="terms-of-service-email"
              >
                collegenav.info@gmail.com
              </a>
              . We strive to respond to all inquiries promptly and thoroughly.
              For legal notices or formal communications, please use the same
              email address and clearly indicate the nature of your
              communication.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
